import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DeviceAccessType } from '@/types/device.types';
import { useDeleteConfirmation } from '@/hooks/useDeleteConfirmation';
import { associationService } from '@/services/api/services/associationService';
import { useToast } from '@/contexts/ToastContext';
import { LoadingOverlay } from '@/components/shared/LoadingOverlay';

interface DisplayInfo {
  mainLabel: string;
  mainValue: string;
  subLabel: string;
  subValue: string;
}

interface EntityDetails {
  id: string;
  name: string;
  description?: string;
  role?: string;
  displayInfo?: DisplayInfo;
}

interface AssociationItem extends EntityDetails {
  typeOfAccess: DeviceAccessType;
}

interface AssociationManagerProps {
  entity: EntityDetails;
  entityType: string;
  currentAssociations: AssociationItem[];
  availableEntities: EntityDetails[];
  onCancel?: () => void;
  onAssociationChange?: (type: 'added' | 'removed', associationId: string) => void;
}

export const AssociationManager: React.FC<AssociationManagerProps> = ({
  entity,
  entityType,
  currentAssociations,
  availableEntities,
  onCancel,
  onAssociationChange,
}) => {
  const { t } = useTranslation();
  const useDeactivateMode = false;
  const { confirmDelete } = useDeleteConfirmation();
  const { showToast } = useToast();
  const [associations, setAssociations] = useState<AssociationItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');

  useEffect(() => {
    setAssociations(currentAssociations);
  }, [currentAssociations]);

  const handleRemoveAssociation = async (associationId: string, associationName: string) => {
    confirmDelete(associationName, 'association', useDeactivateMode, async () => {
      try {
        setIsLoading(true);
        setLoadingMessage(t('pages.associations.removing'));
        const response = await associationService.deleteSingleAssociation(
          entityType === 'user' ? entity.id : associationId,
          entityType === 'user' ? associationId : entity.id
        );

        if (!response.success) {
          throw new Error(response.error?.message || t('pages.associations.errors.removeFailed'));
        }

        setAssociations((prev) => prev.filter((assoc) => assoc.id !== associationId));
        // Notify parent component about the change
        onAssociationChange?.('removed', associationId);
        // Toast de éxito se maneja en useDeleteConfirmation
      } catch (error) {
        showToast(error instanceof Error ? error.message : t('errors.unknown'), 'error');
      } finally {
        setIsLoading(false);
        setLoadingMessage('');
      }
    });
  };

  const handleAddAssociation = async (entityToAdd: EntityDetails) => {
    try {
      setIsLoading(true);
      setLoadingMessage(t('pages.associations.adding'));
      const response = await associationService.createSingleAssociation(
        entityType === 'user' ? entity.id : entityToAdd.id,
        entityType === 'user' ? entityToAdd.id : entity.id,
        DeviceAccessType.VIEWER // Default access type
      );

      if (!response.success) {
        throw new Error(response.error?.message || t('pages.associations.errors.addFailed'));
      }

      const newAssociation: AssociationItem = {
        id: entityToAdd.id,
        name: entityToAdd.name,
        typeOfAccess: DeviceAccessType.VIEWER,
        displayInfo: entityToAdd.displayInfo,
      };

      setAssociations((prev) => [...prev, newAssociation]);
      // Notify parent component about the change
      onAssociationChange?.('added', entityToAdd.id);
      showToast(t('pages.associations.success.added'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('errors.unknown'), 'error');
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  // Filter and validate available entities
  const validAvailableEntities = availableEntities.filter((entity) => {
    const isValid =
      entity &&
      typeof entity.id === 'string' &&
      typeof entity.name === 'string' &&
      !associations.some((a) => a.id === entity.id);

    if (!isValid) {
      console.warn('Invalid available entity found:', entity);
    }

    return isValid;
  });

  return (
    <div className="bg-brand-gray-dark rounded-lg shadow-sm ">
      <div className="p-6">
        {/* Warning Message */}
        <div className="mb-6 p-4 bg-brand-orange bg-opacity-90 border border-brand-orange border-opacity-20 rounded-md">
          <p className="text-brand-white text-sm">{t('pages.associations.automaticWarning')}</p>
        </div>

        {/* Current Associations */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-brand-white mb-4">
            {t('pages.associations.current')}
          </h3>
          <div className="space-y-2">
            {associations.map((assoc) => (
              <div
                key={assoc.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
              >
                <div>
                  {assoc.displayInfo ? (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-brand-gray-dark font-medium">
                          {assoc.displayInfo.mainLabel}
                        </span>
                        <span className="text-brand-gray">{assoc.displayInfo.mainValue}</span>
                      </div>
                      {assoc.displayInfo.subValue && (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-brand-gray-dark font-medium">
                            {assoc.displayInfo.subLabel}
                          </span>
                          <span className="text-brand-gray">{assoc.displayInfo.subValue}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="text-brand-gray-dark">{assoc.name}</span>
                  )}
                </div>
                <button
                  onClick={() => handleRemoveAssociation(assoc.id, assoc.name)}
                  disabled={isLoading}
                  className="bg-white text-red-600 border border-red-600 hover:bg-red-600 hover:text-white px-4 py-2 rounded-md transition-colors duration-200 disabled:opacity-50 text-sm"
                >
                  {t('common.remove')}
                </button>
              </div>
            ))}
            {associations.length === 0 && (
              <p className="text-brand-gray-light italic">
                {t('pages.associations.noAssociations')}
              </p>
            )}
          </div>
        </div>

        {/* Available Entities */}
        <div>
          <h3 className="text-lg font-medium text-brand-white mb-4">
            {t('pages.associations.available')}
          </h3>
          <div className="space-y-2">
            {validAvailableEntities.map((entity) => (
              <div
                key={entity.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
              >
                <div>
                  {entity.displayInfo ? (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-brand-gray-dark font-medium">
                          {entity.displayInfo.mainLabel}
                        </span>
                        <span className="text-brand-gray">{entity.displayInfo.mainValue}</span>
                      </div>
                      {entity.displayInfo.subValue && (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-brand-gray-dark font-medium">
                            {entity.displayInfo.subLabel}
                          </span>
                          <span className="text-brand-gray">{entity.displayInfo.subValue}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="text-brand-gray-dark">{entity.name}</span>
                  )}
                </div>
                <button
                  onClick={() => handleAddAssociation(entity)}
                  disabled={isLoading}
                  className="bg-brand-orange text-white px-4 py-2 rounded-md hover:bg-brand-orange-dark disabled:opacity-50 text-sm"
                >
                  {t('common.add')}
                </button>
              </div>
            ))}
            {validAvailableEntities.length === 0 && (
              <p className="text-brand-gray-light italic">
                {t('pages.associations.noAvailableEntities')}
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={onCancel}
            className="bg-brand-white text-brand-orange border border-brand-orange hover:bg-brand-orange hover:text-brand-white px-6 py-2 rounded-md transition-colors duration-200"
          >
            {t('common.done')}
          </button>
        </div>
      </div>

      {/* Loading Overlay */}
      <LoadingOverlay isVisible={isLoading} message={loadingMessage || t('common.loading')} />
    </div>
  );
};
